import { useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';

// ─── Calendar helpers (mirrors HomePage) ──────────────────────────────────────
function toIsoDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function dateKey(year: number, monthIdx: number, day: number): string {
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function formatDateLabel(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(`${iso}T00:00:00`) : iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}
function formatTimeLabel(t: string): string { return t.slice(0, 5); }

const DAY_NAMES   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March',     'April',   'May',      'June',
  'July',    'August',   'September', 'October', 'November', 'December',
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function BookingPage() {
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [form, setForm] = useState({ topic: '', message: '', mobileNumber: '' });
  const [submitted, setSubmitted] = useState(false);

  // Server returns ALL sessions (incl. full). Used both for the dots and the
  // greyed-out time slots.
  const { data: sessions = [], isLoading } = trpc.sessions.list.useQuery();
  const { data: courses  = [] }            = trpc.courses.list.useQuery();
  const { data: instructors = [] }         = trpc.instructors.list.useQuery();

  const [selectedInstructorId, setSelectedInstructorId] = useState<number | null>(null);

  // Instructor-first: when instructors exist, the calendar only fills in once
  // one is chosen. With no instructors configured, fall back to all sessions.
  const requireInstructor = instructors.length > 0;
  const showCalendar = !requireInstructor || selectedInstructorId != null;
  const visibleSessions = !requireInstructor
    ? sessions
    : selectedInstructorId == null
      ? []
      : sessions.filter(s => (s as any).instructorId === selectedInstructorId);

  const utils = trpc.useUtils();
  const createBooking = trpc.bookings.create.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      setSubmitted(true);
    },
  });

  // ── Calendar state ─────────────────────────────────────────────────────────
  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const monthYear = monthCursor.getFullYear();
  const monthIdx  = monthCursor.getMonth();
  const daysInThisMonth = new Date(monthYear, monthIdx + 1, 0).getDate();
  const dowOfFirst      = new Date(monthYear, monthIdx, 1).getDay();
  const todayIso        = toIsoDate(new Date());

  /** Group sessions by date → counts open/full so we can render the dots. */
  const sessionsByDate = useMemo(() => {
    const map = new Map<string, { open: number; full: number; all: typeof sessions }>();
    for (const s of visibleSessions) {
      const key = toIsoDate(s.date);
      if (!key) continue;
      const bucket = map.get(key) ?? { open: 0, full: 0, all: [] as typeof sessions };
      bucket.all.push(s);
      if (s.availableSlots > 0) bucket.open += 1;
      else                      bucket.full += 1;
      map.set(key, bucket);
    }
    return map;
  }, [visibleSessions]);

  const sessionsForSelectedDate = selectedDate
    ? (sessionsByDate.get(selectedDate)?.all ?? [])
        .slice()
        .sort((a, b) => a.time.localeCompare(b.time))
    : [];

  const goPrevMonth = () => setMonthCursor(new Date(monthYear, monthIdx - 1, 1));
  const goNextMonth = () => setMonthCursor(new Date(monthYear, monthIdx + 1, 1));

  const handleDateClick = (iso: string) => {
    setSelectedDate(iso);
    setSelectedSessionId(null);
  };

  const handleSlotClick = (id: number) => {
    const target = sessions.find(s => s.id === id);
    if (!target || target.availableSlots <= 0) return;
    setSelectedSessionId(id);
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (submitted) {
    const sess = sessions.find(s => s.id === selectedSessionId);
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#FBBF24]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🗓️</span>
          </div>
          <h2 className="text-2xl font-extrabold text-white mb-2">Request received!</h2>
          <p className="text-[#71717A] mb-2">
            {sess ? `${formatDateLabel(sess.date)} at ${formatTimeLabel(sess.time)}` : 'Session pending'}
          </p>
          <p className="text-[#52525B] text-sm mb-6">
            Your booking is pending approval. You will be contacted on the number you provided
            once the session is confirmed.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setSelectedSessionId(null);
              setSelectedDate(null);
              setForm({ topic: '', message: '', mobileNumber: '' });
            }}
            className="bg-[#FBBF24] text-black font-bold px-6 py-2.5 rounded-full"
          >Book Another</button>
        </div>
      </div>
    );
  }

  // ── Main view ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block bg-[#FBBF24]/10 text-[#FBBF24] text-[11px] font-bold
                           tracking-[2px] uppercase px-3 py-1.5 rounded-full
                           border border-[#FBBF24]/20 mb-4">1-on-1</span>
          <h1 className="text-4xl font-extrabold text-white tracking-tight">Book Online Session</h1>
          <p className="text-[#71717A] mt-2">Choose an instructor, then pick a date and time — I'll review and confirm.</p>
        </div>

        {/* Instructor picker — choose one to reveal their availability */}
        {requireInstructor && (
          <div className="mb-10">
            <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest mb-4 text-center">
              Choose an instructor
            </h3>
            <div className="flex flex-wrap justify-center gap-5">
              {instructors.map(ins => {
                const active = selectedInstructorId === ins.id;
                return (
                  <button
                    key={ins.id}
                    onClick={() => {
                      setSelectedInstructorId(active ? null : ins.id);
                      setSelectedDate(null);
                      setSelectedSessionId(null);
                    }}
                    className="flex flex-col items-center gap-2 group w-20"
                  >
                    <div className={`w-20 h-20 rounded-3xl overflow-hidden bg-[#18181B] border-2 transition-all
                                    ${active
                                      ? 'border-[#FBBF24] shadow-[0_0_24px_rgba(234,179,8,0.4)] scale-105'
                                      : 'border-[#27272A] group-hover:border-[#FBBF24]/40'}`}>
                      {(ins as any).iconUrl
                        ? <img src={(ins as any).iconUrl} alt={ins.name} className="w-full h-full object-contain" />
                        : <div className="w-full h-full flex items-center justify-center text-[#3F3F46] text-2xl">?</div>}
                    </div>
                    <span className={`text-xs font-semibold text-center leading-tight
                                     ${active ? 'text-[#FBBF24]' : 'text-[#A1A1AA]'}`}>
                      {ins.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!showCalendar ? (
          <p className="text-center text-[#52525B] text-sm py-10">
            Select an instructor above to see their available dates.
          </p>
        ) : (
        <>
        {/* Month header + prev/next */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest">
            {MONTH_NAMES[monthIdx]} {monthYear}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={goPrevMonth}
              className="w-8 h-8 rounded-xl bg-[#18181B] border border-[#27272A]
                         text-[#A1A1AA] hover:text-white hover:border-[#FBBF24]/40 transition-colors"
              aria-label="Previous month"
            >‹</button>
            <button
              onClick={goNextMonth}
              className="w-8 h-8 rounded-xl bg-[#18181B] border border-[#27272A]
                         text-[#A1A1AA] hover:text-white hover:border-[#FBBF24]/40 transition-colors"
              aria-label="Next month"
            >›</button>
          </div>
        </div>

        {/* Day-of-week header */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[10px] font-bold text-[#52525B] pb-1">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 mb-8">
          {Array.from({ length: dowOfFirst }).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}

          {Array.from({ length: daysInThisMonth }, (_, i) => i + 1).map(day => {
            const iso        = dateKey(monthYear, monthIdx, day);
            const bucket     = sessionsByDate.get(iso);
            const hasOpen    = (bucket?.open ?? 0) > 0;
            const isPast     = iso < todayIso;
            const isSelected = selectedDate === iso;
            const isClickable = hasOpen && !isPast;

            return (
              <button
                key={day}
                disabled={!isClickable}
                onClick={() => isClickable && handleDateClick(iso)}
                className={`relative aspect-square rounded-2xl flex flex-col items-center
                            justify-center transition-all text-sm font-bold
                            ${isPast
                              ? 'text-[#3F3F46] cursor-not-allowed'
                              : isSelected
                                ? 'bg-[#FBBF24] text-black shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                                : isClickable
                                  ? 'bg-[#18181B] text-[#E4E4E7] hover:bg-[#27272A] hover:scale-105'
                                  : 'bg-[#0A0A0B] text-[#52525B] cursor-not-allowed'}`}
              >
                {hasOpen && !isPast && !isSelected && (
                  <span className="absolute top-1.5 left-1/2 -translate-x-1/2
                                   w-1.5 h-1.5 rounded-full bg-[#FBBF24]" />
                )}
                <span>{day}</span>
              </button>
            );
          })}
        </div>

        {/* Time slots for the selected date */}
        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="w-7 h-7 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedDate ? (
          <>
            <h3 className="text-sm font-bold text-[#A1A1AA] uppercase tracking-widest mb-4">
              Available Times — {formatDateLabel(selectedDate)}
            </h3>

            {sessionsForSelectedDate.length === 0 ? (
              <p className="text-[#52525B] text-sm mb-8">
                No sessions scheduled on this date.
              </p>
            ) : (
              <div className="flex flex-wrap gap-4 mb-8">
                {sessionsForSelectedDate.map(s => {
                  const isFull = s.availableSlots <= 0;
                  const isSelected = selectedSessionId === s.id;
                  return (
                    <button
                      key={s.id}
                      disabled={isFull}
                      onClick={() => handleSlotClick(s.id)}
                      style={{ width: 140, height: 140 }}
                      className={`rounded-3xl flex flex-col items-center justify-center
                                  gap-1 transition-all font-bold relative
                                  ${isFull
                                    ? 'bg-[#0A0A0B] text-[#52525B] cursor-not-allowed border border-[#27272A]/60'
                                    : isSelected
                                      ? 'bg-[#FBBF24] text-black shadow-[0_0_24px_rgba(234,179,8,0.45)]'
                                      : 'bg-[#18181B] text-white hover:bg-[#27272A] hover:scale-105'}`}
                    >
                      <span className="text-lg leading-tight">{formatTimeLabel(s.time)}</span>
                      <span className={`text-[11px] font-bold
                                        ${isFull
                                          ? 'text-[#52525B]'
                                          : isSelected ? 'text-black' : 'text-[#FBBF24]'}`}>
                        {parseFloat((s as any).price as string) === 0
                          ? 'Free'
                          : `${(s as any).price} KD`}
                      </span>
                      <span className={`text-[10px] mt-0.5 font-medium
                                        ${isFull
                                          ? 'text-[#52525B]'
                                          : isSelected ? 'text-black/60' : 'text-[#A1A1AA]'}`}>
                        {isFull
                          ? 'Fully booked'
                          : `${s.availableSlots}/${s.capacity} open`}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <p className="text-[#52525B] text-sm mb-8">
            Select a date with availability (•) to see open time slots.
          </p>
        )}

        {/* Booking form — appears when a slot is picked */}
        {selectedSessionId && (() => {
          const sel = sessions.find(s => s.id === selectedSessionId);
          if (!sel) return null;
          const priceStr = parseFloat((sel as any).price as string) === 0
            ? 'Free' : `${(sel as any).price} KD`;
          return (
            <div className="bg-[#18181B] rounded-2xl border border-[#27272A] p-8">
              <h2 className="text-xl font-extrabold text-white mb-1">Session Details</h2>
              <p className="text-[#71717A] text-sm mb-4">
                {formatDateLabel(sel.date)} · {formatTimeLabel(sel.time)}
              </p>
              <div className="flex items-center justify-between bg-black
                              border border-[#27272A] rounded-xl px-4 py-3 mb-6">
                <span className="text-[#A1A1AA] text-sm">Price</span>
                <span className="text-[#FBBF24] font-black text-lg">{priceStr}</span>
              </div>
              <div className="h-px bg-white/[0.05] mb-6" />

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createBooking.mutate({ sessionId: selectedSessionId, ...form });
                }}
                className="space-y-5"
              >
                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-[0.5px] mb-2">
                    Subject / Topic <span className="text-[#FBBF24]">*</span>
                  </label>
                  <select
                    value={
                      courses.some(c => c.title === form.topic)
                        ? form.topic
                        : form.topic === '' ? '' : '__other__'
                    }
                    onChange={e => {
                      const v = e.target.value;
                      setForm(f => ({ ...f, topic: v === '__other__' ? '' : v }));
                    }}
                    required
                    className="w-full mp-card rounded-xl
                               text-white px-4 py-3 text-sm outline-none
                               focus:border-[#FBBF24]/40"
                  >
                    <option value="">Select a course…</option>
                    {courses.map(c => (
                      <option key={c.id} value={c.title}>{c.title}</option>
                    ))}
                    <option value="__other__">Other</option>
                  </select>
                  {form.topic !== '' && !courses.some(c => c.title === form.topic) && (
                    <input
                      value={form.topic}
                      onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                      required
                      placeholder="Describe your topic"
                      className="w-full mp-card rounded-xl mt-2
                                 text-white px-4 py-3 text-sm outline-none
                                 focus:border-[#FBBF24]/40 placeholder-zinc-600"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-[0.5px] mb-2">
                    Description <span className="text-[#FBBF24]">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    required
                    rows={4}
                    placeholder="Describe what you want to cover (min 10 characters)..."
                    className="w-full mp-card rounded-xl
                               text-white px-4 py-3 text-sm outline-none resize-none
                               focus:border-[#FBBF24]/40 placeholder-zinc-600"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-[#71717A] uppercase tracking-[0.5px] mb-2">
                    Your Phone Number <span className="text-[#FBBF24]">*</span>
                  </label>
                  <input
                    type="tel"
                    value={form.mobileNumber}
                    onChange={e => setForm(f => ({ ...f, mobileNumber: e.target.value }))}
                    required
                    placeholder="+965 XXXX XXXX"
                    className="w-full mp-card rounded-xl
                               text-white px-4 py-3 text-sm outline-none
                               focus:border-[#FBBF24]/40 placeholder-zinc-600"
                  />
                </div>

                {createBooking.error && (
                  <p className="text-red-400 text-sm">{createBooking.error.message}</p>
                )}

                <button
                  type="submit"
                  disabled={createBooking.isPending}
                  className="w-full py-3.5 bg-[#FBBF24] hover:bg-[#FBBF24] disabled:opacity-50
                             text-black font-bold rounded-xl text-sm transition-colors"
                >
                  {createBooking.isPending ? 'Sending…' : 'Submit booking request'}
                </button>
              </form>
            </div>
          );
        })()}
        </>
        )}
      </div>
    </div>
  );
}
